import useLatestRequest from '../hooks/useLatestRequest';
import RefreshRegion from '../components/common/RefreshRegion';
import React, { useState, useEffect, useMemo } from 'react';
import { motion } from 'framer-motion';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Input from '../components/ui/Input';
import { 
  CalendarIcon, 
  ClockIcon, 
  DocumentArrowDownIcon,
  FunnelIcon
} from '@heroicons/react/24/outline';
import AttendanceService from '../services/attendance.service';
import toast from '../utils/toast';
import { useHeaderSearch } from '../contexts/SearchContext';
import { formatTime12hFromDb } from '../utils/timeDisplay';
import { formatDateDisplay } from '../utils/dateDisplay';

const formatDurationForUi = (timeValue) => {
  if (!timeValue || typeof timeValue !== 'string') return '0m';

  const parts = timeValue.split(':').map(Number);
  const hours = Number.isNaN(parts[0]) ? 0 : parts[0];
  const minutes = Number.isNaN(parts[1]) ? 0 : parts[1];
  const totalMinutes = (hours * 60) + minutes;

  if (totalMinutes <= 0) return '0m';
  if (totalMinutes < 60) return `${totalMinutes}m`;

  const labelHours = Math.floor(totalMinutes / 60);
  const labelMinutes = totalMinutes % 60;
  return labelMinutes === 0 ? `${labelHours}h` : `${labelHours}h ${labelMinutes}m`;
};

const getNetWorkTime = (record) => {
  if (!record?.intime || !record?.outtime) return record?.worktime || '00:00:00';

  const calculated = AttendanceService.calculateWorkHours(
    record.intime,
    record.outtime,
    record.breaktime || '00:00:00'
  );

  return calculated || record?.worktime || '00:00:00';
};

const isMeaningfulTime = (value) => {
  if (!value) return false;
  const text = String(value).trim();
  if (!text) return false;
  return text !== '00:00' && text !== '00:00:00';
};

const parseHmsToSeconds = (value) => {
  if (!isMeaningfulTime(value)) return 0;
  const [h = 0, m = 0, s = 0] = String(value)
    .trim()
    .split(':')
    .map((part) => Number(part));
  return (Number.isFinite(h) ? h : 0) * 3600 + (Number.isFinite(m) ? m : 0) * 60 + (Number.isFinite(s) ? s : 0);
};

const REQUIRED_WORK_SECONDS_FOR_PRESENT = 9 * 60 * 60;

const displayClockFromDb = (value) => {
  if (!isMeaningfulTime(value)) return '--:--';
  return formatTime12hFromDb(value);
};

const getFrontendCalculatedStatus = (record, netWorkTime) => {
  const rawStatus = typeof record?.status === 'string' ? record.status.trim() : '';

  // Keep the weekly-off value for backend reporting, but use the requested label in Attendance Records.
  if (rawStatus === 'WO') return 'Holiday';

  // Apply the same completed-punch rule to managed and unmanaged work days.
  // Keep non-work statuses such as Holiday/Leave/Absent/WFH as-is.
  if (rawStatus && rawStatus !== 'Present' && rawStatus !== 'HalfDay') return rawStatus;

  // Avoid marking HalfDay while the day is still running (no out time yet).
  if (!isMeaningfulTime(record?.intime) || !isMeaningfulTime(record?.outtime)) return rawStatus;

  const netSeconds = parseHmsToSeconds(netWorkTime);
  if (netSeconds <= 0) return rawStatus;

  return netSeconds >= REQUIRED_WORK_SECONDS_FOR_PRESENT ? 'Present' : 'HalfDay';
};

const AttendanceHistory = () => {
  const { headerSearch } = useHeaderSearch();
  const [attendanceData, setAttendanceData] = useState([]);
  const { loading, begin: beginFetchAttendanceHistory } = useLatestRequest();
  const [dateRange, setDateRange] = useState({
    startDate: '',
    endDate: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(10);

  useEffect(() => {
    // Set default date range to last 30 days
    const endDate = new Date();
    const startDate = new Date();
    startDate.setDate(endDate.getDate() - 30);
    
    setDateRange({
      startDate: formatDateForInput(startDate),
      endDate: formatDateForInput(endDate)
    });
    
    fetchAttendanceHistory(formatDate(startDate), formatDate(endDate));
  }, []);

  const formatDate = (date) => {
    const dd = String(date.getDate()).padStart(2, '0');
    const mm = String(date.getMonth() + 1).padStart(2, '0');
    const yyyy = date.getFullYear();
    return `${dd}/${mm}/${yyyy}`;
  };

  const formatDateForInput = (date) => {
    return date.toISOString().split('T')[0];
  };

  const fetchAttendanceHistory = async (startDate, endDate) => {
    const request = beginFetchAttendanceHistory();
    try {
      const response = await AttendanceService.getAttendance(startDate, endDate);
      if (!request.isCurrent()) return;
      setAttendanceData(response.attendanceData || []);
    } catch (error) {
      if (!request.isCurrent()) return;
      console.error('Error fetching attendance history:', error);
      toast.error('Failed to load attendance history');
    } finally {
      request.finish();
    }
  };

  const handleDateRangeChange = () => {
    if (dateRange.startDate && dateRange.endDate) {
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      setCurrentPage(1);
      fetchAttendanceHistory(formatDate(start), formatDate(end));
    }
  };

  const handleExport = async () => {
    try {
      if (!dateRange.startDate || !dateRange.endDate) {
        toast.error('Please select a date range');
        return;
      }
      
      const start = new Date(dateRange.startDate);
      const end = new Date(dateRange.endDate);
      
      const blob = await AttendanceService.exportAttendanceReport(
        formatDate(start),
        formatDate(end),
        'excel'
      );
      
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `attendance-history-${formatDate(start)}-to-${formatDate(end)}.xlsx`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Failed to export report');
    }
  };

  // Reset page when header search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [headerSearch]);

  // Filter by header search
  const decoratedData = useMemo(() => {
    return (attendanceData || []).map((record) => {
      const netWorkTime = getNetWorkTime(record);
      const displayStatus = getFrontendCalculatedStatus(record, netWorkTime) || record?.status || '';
      return {
        ...record,
        __netWorkTime: netWorkTime,
        __displayStatus: displayStatus,
      };
    });
  }, [attendanceData]);

  const filteredData = useMemo(() => {
    if (!headerSearch) return decoratedData;
    const q = headerSearch.toLowerCase();
    return decoratedData.filter((record) => {
      const dateStr = record.datestring ? formatDateDisplay(record.datestring) : '';
      return (
        (record.__displayStatus || '').toLowerCase().includes(q) ||
        dateStr.toLowerCase().includes(q) ||
        (record.remark || '').toLowerCase().includes(q) ||
        (record.intime || '').toLowerCase().includes(q) ||
        (record.outtime || '').toLowerCase().includes(q)
      );
    });
  }, [decoratedData, headerSearch]);

  // Pagination
  const totalPages = Math.max(1, Math.ceil(filteredData.length / itemsPerPage));
  const activePage = Math.min(currentPage, totalPages);
  const indexOfLastItem = activePage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredData.slice(indexOfFirstItem, indexOfLastItem);

  useEffect(() => {
    if (currentPage !== activePage) {
      setCurrentPage(activePage);
    }
  }, [currentPage, activePage]);

  const paginate = (pageNumber) => setCurrentPage(Math.max(1, Math.min(pageNumber, totalPages)));

  const getStatusBadge = (status) => {
    const statusClasses = {
      'Present': 'bg-green-100 text-green-800',
      'Absent': 'bg-red-100 text-red-800',
      'Holiday': 'bg-blue-100 text-blue-800',
      'Leave': 'bg-yellow-100 text-yellow-800',
      'PaidLeave': 'bg-purple-100 text-purple-800',
      'HalfDay': 'bg-orange-100 text-orange-800'
    };
    
    return (
      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${statusClasses[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };


  return (
    <div className="space-y-6">
      {/* Header */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-xl p-6 text-white"
      >
        <h1 className="text-3xl font-bold mb-2">Attendance History</h1>
        <p className="text-blue-100">View and manage your attendance records</p>
      </motion.div>

      {/* Filters */}
      <Card>
        <Card.Header>
          <h3 className="text-lg font-semibold">Filter Records</h3>
        </Card.Header>
        <Card.Body>
          <div className="flex items-center gap-4 flex-wrap">
            <div className="flex items-center gap-2 text-gray-600">
              <FunnelIcon className="w-5 h-5" />
              <span className="text-sm font-medium">Date Range:</span>
            </div>
            
            <Input
              type="date"
              value={dateRange.startDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, startDate: e.target.value }))}
              className="w-auto"
            />
            
            <span className="text-gray-500">to</span>
            
            <Input
              type="date"
              value={dateRange.endDate}
              onChange={(e) => setDateRange(prev => ({ ...prev, endDate: e.target.value }))}
              className="w-auto"
            />
            
            <Button onClick={handleDateRangeChange}>
              Apply Filter
            </Button>
            
            <Button
              variant="outline"
              icon={DocumentArrowDownIcon}
              onClick={handleExport}
              className="ml-auto"
            >
              Export
            </Button>
          </div>
        </Card.Body>
      </Card>

      {/* Attendance Table */}
      <RefreshRegion loading={loading}>
      <Card>
        <Card.Header>
          <div className="flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">Attendance Records</h3>
              <p className="text-sm text-gray-600">
                Showing {currentItems.length} of {filteredData.length} records
              </p>
            </div>
            <div className="flex items-center gap-2">
              <CalendarIcon className="w-5 h-5 text-gray-400" />
              <span className="text-sm text-gray-600">
                Page {activePage} of {totalPages}
              </span>
            </div>
          </div>
        </Card.Header>
        <Card.Body className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    In Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Out Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Work Hours
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Break Time
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Overtime
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {currentItems.length > 0 ? (
                  currentItems.map((record, index) => (
                    <tr key={index} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDateDisplay(record.datestring)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        {getStatusBadge(record.__displayStatus || record.status)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {displayClockFromDb(record.intime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {displayClockFromDb(record.outtime)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDurationForUi(record.__netWorkTime || getNetWorkTime(record))}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {formatDurationForUi(record.breaktime || '00:00:00')}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {record.overtime || '00:00:00'}
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="7" className="px-6 py-12 text-center text-gray-500">
                      <div className="flex flex-col items-center">
                        <ClockIcon className="w-12 h-12 text-gray-300 mb-4" />
                        <p className="text-lg font-medium">No attendance records found</p>
                        <p className="text-sm">Try adjusting your date range</p>
                      </div>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {totalPages > 1 && (
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <div className="text-sm text-gray-700">
                Showing {indexOfFirstItem + 1} to {Math.min(indexOfLastItem, filteredData.length)} of {filteredData.length} results
              </div>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => paginate(activePage - 1)}
                  disabled={activePage === 1}
                >
                  Previous
                </Button>
                
                <div className="flex items-center gap-1">
                  {[...Array(Math.min(5, totalPages))].map((_, index) => {
                    const pageNumber = activePage <= 3 
                      ? index + 1 
                      : activePage + index - 2;
                    
                    if (pageNumber > totalPages) return null;
                    
                    return (
                      <button
                        key={pageNumber}
                        onClick={() => paginate(pageNumber)}
                        className={`px-3 py-1 text-sm rounded-md transition-colors ${
                          activePage === pageNumber
                            ? 'bg-blue-600 text-white'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`}
                      >
                        {pageNumber}
                      </button>
                    );
                  })}
                </div>
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => paginate(activePage + 1)}
                  disabled={activePage === totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </Card.Body>
      </Card>
      </RefreshRegion>
    </div>
  );
};

export default AttendanceHistory;
